/**
 * Pente multiplayer room.
 *
 * Opens a WebSocket to /api/pente/ws/:key, authenticates with the
 * visitor's display name (read from localStorage["pente:name"]), and
 * renders the canonical server-pushed state.
 *
 * The server is the source of truth — clicks send `{type:"place", x, y}`
 * to the WS and the state is then rebroadcast to every connected
 * client.  Clients render-only.
 *
 * Seats:
 *   - First joiner is Black, second is White, third+ spectate.
 *   - Disconnect leaves the seat reserved by name so a refresh
 *     reconnects to the same colour.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy } from "lucide-react";
import { wsBase } from "@/lib/api";
import { playGemPlace } from "@/lib/sounds";
import TopBar from "@/components/TopBar";
import NavPane from "@/components/NavPane";
import { useEerieBackdrop } from "@/lib/eerie";

const SIZE = 19;
const FONT = "'JetBrains Mono','Lucida Console','Courier New',monospace";
const PAL = {
  bg:    "radial-gradient(ellipse 80% 60% at 50% 35%, #1B1340 0%, #0A0826 50%, #03020E 100%)",
  panel: "rgba(8,6,28,0.7)",
  border:"#7B6CFF",
  accent:"#9DC0FF",
  ink:   "#E6E2FF",
  muted: "#8A8AB5",
  grid:  "rgba(157,192,255,0.32)",
  starpoint: "rgba(157,192,255,0.55)",
  // Stones are now glowing GEMS — sapphire (blue) for the side that
  // used to play black, ruby (red) for the side that used to play white.
  rubyCore:    "#FF1F3A",
  rubyHi:      "#FFD8DE",
  rubyShadow:  "#5C0010",
  sapphireCore:    "#1A52FF",
  sapphireHi:      "#CFE0FF",
  sapphireShadow:  "#031040",
  hi:    "rgba(232,221,184,0.6)",
};
const LETTERS = "ABCDEFGHJKLMNOPQRST";
const coordLabel = (x, y) => `${LETTERS[y]}${SIZE - x}`;

export default function PenteRoom() {
  useEerieBackdrop("pente");
  const navigate = useNavigate();
  const { key } = useParams();
  const wsRef = useRef(null);

  const [state, setState] = useState(null);   // server state
  const [you, setYou] = useState(null);        // 'B' | 'W' | 'spectator'
  const [yourName, setYourName] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [cell, setCell] = useState(28);

  const myName = useMemo(
    () => (localStorage.getItem("pente:name") || "Stranger").slice(0, 30),
    []
  );

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setCell(w <= 640 ? 18 : w <= 900 ? 22 : 28);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!key) return;
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let cancelled = false;
    let attempt = 0;
    let lastMoveSig = "";        // signature of last received move — used to fire a click sound only on actual new placements

    const connect = () => {
      if (cancelled) return;
      const url = `${wsBase()}/pente/ws/${encodeURIComponent(key.toUpperCase())}`;
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        attempt = 0;
        ws.send(JSON.stringify({ type: "join", name: myName }));
        // Heartbeat: send a tiny ping every 25 s so idle proxies don't
        // close the socket on us.
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          try {
            if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
          } catch { /* */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "state") {
            // Detect a *new* placement vs. previous state for the gem
            // sound — fire on every stone-on-board increase, regardless
            // of whether we placed it or the opponent did.
            const sig = `${msg.move_num || 0}:${(msg.last_move || []).join(",")}`;
            if (lastMoveSig && sig !== lastMoveSig && msg.last_move) {
              try { playGemPlace(); } catch { /* */ }
            }
            lastMoveSig = sig;
            setState(msg);
            setYou(msg.you);
            setYourName(msg.your_name);
          } else if (msg.type === "error") {
            setError(msg.error || "An error occurred.");
            if (msg.error !== "room_not_found") {
              setTimeout(() => setError(null), 2200);
            }
          }
        } catch { /* ignore malformed */ }
      };
      ws.onerror = () => { /* surfaced by close */ };
      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        // Auto-reconnect with simple backoff; cap the wait at 5 s so a
        // dropped match recovers quickly.
        attempt += 1;
        const wait = Math.min(5000, 400 * attempt);
        reconnectTimer = setTimeout(connect, wait);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      try { ws && ws.close(); } catch { /* */ }
    };
  }, [key, myName]);

  const place = (x, y) => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (you !== "B" && you !== "W") return;
    if (!state) return;
    if (state.winner) return;
    wsRef.current.send(JSON.stringify({ type: "place", x, y }));
  };
  const resign = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (you !== "B" && you !== "W") return;
    if (state?.winner) return;
    if (!window.confirm("Resign this match?")) return;
    wsRef.current.send(JSON.stringify({ type: "resign" }));
  };
  const newGame = () => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (!state?.winner) return;
    wsRef.current.send(JSON.stringify({ type: "new_game" }));
  };
  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(key.toUpperCase());
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 1500);
    } catch { /* clipboard may be blocked */ }
  };

  // ----- Derived render data -----
  const board = state?.board || Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  const lastMove = state?.last_move || null;
  const winLineSet = useMemo(() => {
    const s = new Set();
    if (state?.win_line) for (const [x, y] of state.win_line) s.add(`${x},${y}`);
    return s;
  }, [state]);

  const pad = cell;
  const boardPx = pad * 2 + cell * (SIZE - 1);
  const stoneR = cell * 0.42;
  const hoshi = [];
  for (const r of [3, 9, 15]) for (const c of [3, 9, 15]) hoshi.push([r, c]);

  let statusLine = "Connecting…";
  let emphasise = false;
  if (error === "room_not_found") {
    statusLine = "Match not found. Return to the lobby.";
    emphasise = true;
  } else if (!connected) {
    statusLine = "Connecting…";
  } else if (!state) {
    statusLine = "Joining match…";
  } else if (state.winner) {
    const winSide = state.winner === "B" ? "Black" : "White";
    const myWin = (you === "B" || you === "W") && state.winner === you;
    statusLine = state.win_line && state.win_line.length >= 5
      ? `${winSide} wins — five in a row.${myWin ? "  (You win!)" : ""}`
      : `${winSide} wins — five pairs captured.${myWin ? "  (You win!)" : ""}`;
    emphasise = true;
  } else {
    const seatedB = !!state.seats?.B;
    const seatedW = !!state.seats?.W;
    if (!seatedB || !seatedW) {
      statusLine = `Waiting for opponent…  Share the match-key.`;
    } else if (state.move_num === 0) {
      const blackName = state.seats?.B || "Black";
      statusLine = you === "B"
        ? "You play Black. Open at the centre intersection."
        : `${blackName} (Black) opens at the centre.`;
    } else {
      const turnSide = state.turn === "B" ? "Black" : "White";
      const turnName = state.seats?.[state.turn] || turnSide;
      statusLine = state.turn === you
        ? `Your turn (${turnSide}).`
        : `${turnName} (${turnSide}) is to play.`;
    }
  }

  const myColourLabel = you === "B" ? "Black" : you === "W" ? "White" : "Spectator";

  return (
    <div
      className="relative min-h-screen overflow-x-clip pente-stage-bg"
      data-testid="pente-room-page"
      style={{
        backgroundColor: "#03020E",
        backgroundImage: "url('/pente/space-earth.jpg')",
        color: PAL.ink,
        fontFamily: FONT,
      }}
    >
      {/* Subtle dark veil so the gameboard reads cleanly on top. */}
      <div
        className="pointer-events-none absolute inset-0 pente-bg-veil"
        aria-hidden="true"
      />
      <TopBar theme="studio" onBack={() => navigate("/games/pente")} backLabel="LOBBY" testId="pente-room-back-button" />

      <div className="relative z-10 max-w-7xl mx-auto flex flex-col md:flex-row">
        <div className="md:w-[260px] md:shrink-0 md:min-h-screen flex flex-col pt-4 md:pt-6 px-4 md:px-6 pb-6 md:border-r" style={{ borderColor: `${PAL.accent}26` }}>
          <NavPane theme="studio" />
        </div>

        <main className="flex-1 px-6 md:px-12 pt-10 pb-16">
          <div className="text-[10px] tracking-[0.4em] uppercase mb-3" style={{ color: PAL.accent }}>
            Multiplayer · Match {key?.toUpperCase()}
          </div>
          <h1
            className="mb-2"
            data-testid="pente-room-title"
            style={{
              fontFamily: FONT, fontWeight: 700, color: PAL.ink,
              fontSize: "clamp(1.6rem, 3vw, 2.4rem)", lineHeight: 1.05,
              letterSpacing: "0.02em",
              textShadow: "0 2px 0 rgba(0,0,0,0.6), 0 0 22px rgba(157,192,255,0.35)",
            }}
          >
            {state?.name || "Pente"}
          </h1>

          {/* Match-key share row */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="text-xs tracking-[0.3em] uppercase" style={{ color: PAL.muted }}>
              Match-key
            </div>
            <div
              data-testid="pente-room-key"
              style={{
                background: PAL.panel, border: `1px solid ${PAL.border}`,
                padding: "4px 12px", letterSpacing: "0.4em", fontSize: 16,
                fontFamily: FONT, color: PAL.ink,
              }}
            >
              {key?.toUpperCase()}
            </div>
            <button
              onClick={copyKey}
              data-testid="pente-copy-key"
              className="text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1"
              style={{ color: PAL.accent, fontFamily: FONT }}
            >
              <Copy size={12} /> {keyCopied ? "Copied!" : "Copy"}
            </button>
            <div className="flex-1" />
            <div className="text-xs" style={{ color: PAL.muted }}>
              You: <span style={{ color: PAL.ink }}>{yourName || myName}</span>
              {" · "}{myColourLabel}
            </div>
          </div>

          <div className="flex flex-col xl:flex-row gap-8">
            <div>
              <div
                className="flex items-center gap-6 mb-3 text-xs tracking-[0.2em] uppercase"
                style={{ color: PAL.muted }}
              >
                <span data-testid="pente-pairs-black">
                  <span style={{ color: PAL.sapphireCore }}>◆</span> {state?.seats?.B || "Sapphire"}: {state?.pairs?.B ?? 0} pairs
                </span>
                <span data-testid="pente-pairs-white">
                  <span style={{ color: PAL.rubyCore }}>◆</span> {state?.seats?.W || "Ruby"}: {state?.pairs?.W ?? 0} pairs
                </span>
              </div>

              <svg
                width={boardPx}
                height={boardPx}
                viewBox={`0 0 ${boardPx} ${boardPx}`}
                style={{
                  background: "rgba(7,5,30,0.55)",
                  border: `2px solid ${PAL.border}`,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.55)",
                }}
                data-testid="pente-board"
              >
                {/* Per-gem radial gradients — defined once and reused. */}
                <defs>
                  <radialGradient id="ruby-grad" cx="0.30" cy="0.25" r="0.95">
                    <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="0.92" />
                    <stop offset="14%" stopColor={PAL.rubyHi}     stopOpacity="0.95" />
                    <stop offset="42%" stopColor={PAL.rubyCore}   stopOpacity="1" />
                    <stop offset="80%" stopColor="#A3001A"        stopOpacity="1" />
                    <stop offset="100%" stopColor={PAL.rubyShadow} stopOpacity="1" />
                  </radialGradient>
                  <radialGradient id="sapphire-grad" cx="0.30" cy="0.25" r="0.95">
                    <stop offset="0%"  stopColor="#FFFFFF" stopOpacity="0.92" />
                    <stop offset="14%" stopColor={PAL.sapphireHi}     stopOpacity="0.95" />
                    <stop offset="42%" stopColor={PAL.sapphireCore}   stopOpacity="1" />
                    <stop offset="80%" stopColor="#072099"            stopOpacity="1" />
                    <stop offset="100%" stopColor={PAL.sapphireShadow} stopOpacity="1" />
                  </radialGradient>
                  <linearGradient id="ruby-facet-a" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FFE4E8" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#FFE4E8" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="sapphire-facet-a" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#DEEAFF" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#DEEAFF" stopOpacity="0" />
                  </linearGradient>
                  <filter id="ruby-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3.5" result="b" />
                    <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="sapphire-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3.5" result="b" />
                    <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                {Array.from({ length: SIZE }, (_, i) => (
                  <g key={`g${i}`}>
                    <line x1={pad} y1={pad + i * cell} x2={pad + cell * (SIZE - 1)} y2={pad + i * cell} stroke={PAL.grid} strokeWidth={1} />
                    <line x1={pad + i * cell} y1={pad} x2={pad + i * cell} y2={pad + cell * (SIZE - 1)} stroke={PAL.grid} strokeWidth={1} />
                  </g>
                ))}
                {hoshi.map(([r, c], i) => (
                  <circle key={`h${i}`} cx={pad + c * cell} cy={pad + r * cell} r={2.5} fill={PAL.starpoint} />
                ))}
                {lastMove && (
                  <circle
                    cx={pad + lastMove[1] * cell}
                    cy={pad + lastMove[0] * cell}
                    r={stoneR + 4} fill="none" stroke={PAL.hi} strokeWidth={1.5} opacity={0.85}
                  />
                )}
                {board.map((row, x) =>
                  row.map((c, y) => {
                    if (!c) return null;
                    const inWin = winLineSet.has(`${x},${y}`);
                    const cx = pad + y * cell;
                    const cy = pad + x * cell;
                    const isB = c === "B";
                    const gradId = isB ? "sapphire-grad" : "ruby-grad";
                    const filterId = isB ? "url(#sapphire-glow)" : "url(#ruby-glow)";
                    const auraColor = isB ? "rgba(50,120,255,0.55)" : "rgba(255,60,70,0.55)";
                    return (
                      <g key={`s${x}-${y}`} data-testid={`pente-stone-${x}-${y}`} filter={filterId}>
                        {/* Large soft outer aura */}
                        <circle cx={cx} cy={cy} r={stoneR * 1.7} fill={auraColor} opacity={0.55} />
                        {/* Smaller intense aura */}
                        <circle cx={cx} cy={cy} r={stoneR * 1.25} fill={auraColor} opacity={0.7} />
                        {/* Faceted gem body — radial gradient base */}
                        <circle cx={cx} cy={cy} r={stoneR}
                          fill={`url(#${gradId})`}
                          stroke={isB ? "rgba(220,235,255,0.7)" : "rgba(255,220,220,0.7)"} strokeWidth={1.2} />

                        {/* === Brilliant-cut facet pattern ===
                            Eight triangular crown facets radiating from
                            the centre to evenly-spaced rim points.
                            Alternating fills (light highlight / mid
                            shade) build the kaleidoscopic look of a
                            real cut jewel. */}
                        {Array.from({ length: 8 }).map((_, k) => {
                          const aStart = (k / 8) * Math.PI * 2 - Math.PI / 2;
                          const aEnd   = ((k + 1) / 8) * Math.PI * 2 - Math.PI / 2;
                          const rOuter = stoneR * 0.96;
                          const x1 = cx + Math.cos(aStart) * rOuter;
                          const y1 = cy + Math.sin(aStart) * rOuter;
                          const x2 = cx + Math.cos(aEnd)   * rOuter;
                          const y2 = cy + Math.sin(aEnd)   * rOuter;
                          // Mid radius point for a slightly concave look.
                          const mx = cx + Math.cos((aStart + aEnd) / 2) * stoneR * 0.45;
                          const my = cy + Math.sin((aStart + aEnd) / 2) * stoneR * 0.45;
                          // Per-facet shading: 0..7 cycles bright→dim→bright
                          // so the jewel reads as kaleidoscopic.
                          const lit = k % 2 === 0;
                          const facetFill = isB
                            ? (lit ? "rgba(220,235,255,0.55)" : "rgba(20,60,180,0.45)")
                            : (lit ? "rgba(255,225,225,0.55)" : "rgba(160,10,30,0.45)");
                          return (
                            <polygon
                              key={`fc${x}-${y}-${k}`}
                              points={`${cx},${cy} ${x1},${y1} ${mx},${my} ${x2},${y2}`}
                              fill={facetFill}
                              stroke={isB ? "rgba(255,255,255,0.18)" : "rgba(255,200,200,0.22)"}
                              strokeWidth={0.6}
                              opacity={0.95}
                            />
                          );
                        })}

                        {/* Inner table — small bright octagon at the
                            centre acts as the cut's "table" facet,
                            catching the most light. */}
                        {(() => {
                          const tablePts = Array.from({ length: 8 }).map((_, k) => {
                            const a = (k / 8) * Math.PI * 2 - Math.PI / 2 + Math.PI / 8;
                            return `${cx + Math.cos(a) * stoneR * 0.32},${cy + Math.sin(a) * stoneR * 0.32}`;
                          }).join(" ");
                          return (
                            <polygon
                              points={tablePts}
                              fill={isB ? "rgba(220,235,255,0.55)" : "rgba(255,225,225,0.55)"}
                              stroke={isB ? "rgba(255,255,255,0.7)" : "rgba(255,210,210,0.7)"}
                              strokeWidth={0.6}
                            />
                          );
                        })()}

                        {/* Top-left soft highlight (the gem's principal lustre) */}
                        <ellipse cx={cx - stoneR * 0.30} cy={cy - stoneR * 0.36}
                          rx={stoneR * 0.42} ry={stoneR * 0.22}
                          fill="#FFFFFF" opacity={0.85} />
                        {/* Pinpoint specular */}
                        <circle cx={cx - stoneR * 0.20} cy={cy - stoneR * 0.30} r={stoneR * 0.10} fill="#FFFFFF" opacity={1} />
                        {/* Tiny secondary sparkle */}
                        <circle cx={cx + stoneR * 0.45} cy={cy + stoneR * 0.32} r={stoneR * 0.06} fill="#FFFFFF" opacity={0.9} />
                        {inWin && (
                          <circle cx={cx} cy={cy} r={stoneR + 2} fill="none" stroke="#FFD976" strokeWidth={2} />
                        )}
                      </g>
                    );
                  })
                )}
                {Array.from({ length: SIZE }, (_, x) =>
                  Array.from({ length: SIZE }, (_, y) => {
                    const occupied = board[x][y] !== null;
                    const myTurn = state && !state.winner && state.turn === you && (you === "B" || you === "W");
                    const blockedFirstMove = state && state.move_num === 0 && (x !== 9 || y !== 9);
                    const blocked = !myTurn || occupied || blockedFirstMove;
                    return (
                      <circle
                        key={`o${x}-${y}`}
                        cx={pad + y * cell} cy={pad + x * cell} r={cell * 0.45}
                        fill="transparent"
                        style={{ cursor: blocked ? "default" : "pointer" }}
                        onClick={() => !blocked && place(x, y)}
                        data-testid={`pente-click-${x}-${y}`}
                      />
                    );
                  })
                )}
              </svg>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <div
                  data-testid="pente-status"
                  className="text-sm"
                  style={{
                    color: emphasise ? "#FFD976" : PAL.accent,
                    fontWeight: emphasise ? 700 : 500,
                    letterSpacing: "0.02em",
                  }}
                >
                  {statusLine}
                </div>
                <div className="flex-1" />
                {state?.winner ? (
                  <button
                    onClick={newGame}
                    data-testid="pente-new-game"
                    disabled={you !== "B" && you !== "W"}
                    className="px-3 py-1.5 text-xs tracking-[0.25em] uppercase"
                    style={{
                      background: PAL.ink, color: "#0A0826",
                      border: `1px solid ${PAL.border}`, fontWeight: 700, fontFamily: FONT,
                      opacity: you !== "B" && you !== "W" ? 0.4 : 1,
                    }}
                  >
                    New Game (swap colours)
                  </button>
                ) : (
                  <button
                    onClick={resign}
                    disabled={you !== "B" && you !== "W"}
                    data-testid="pente-resign"
                    className="px-3 py-1.5 text-xs tracking-[0.25em] uppercase"
                    style={{
                      background: "transparent", color: PAL.accent,
                      border: `1px solid ${PAL.border}`, fontWeight: 600, fontFamily: FONT,
                      opacity: you !== "B" && you !== "W" ? 0.4 : 1,
                    }}
                  >
                    Resign
                  </button>
                )}
              </div>

              {error && error !== "room_not_found" && (
                <div className="mt-3 text-xs" style={{ color: "#FF8B8B" }} data-testid="pente-room-error">
                  {error}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
